/**
 * A client's read-modify-write of the tenant's alert settings (D37): what the
 * CLI and the SPA may send back of the settings they read. Client logic over
 * the contract's shapes, shared by both clients (moved out of
 * `@acp/api-contract` in cleanup B).
 */
import { alertTypeWireSchema, type AlertSettingsWire, type SiteOverrideWire, type UpdateAlertSettingsRequestWire } from '@acp/api-contract/alerts.js';

/**
 * The rules and site overrides a client may send back of the settings it read (D37): every type whose rule and
 * overrides it can write. A type holding a severity the client does not know (`unknown`) is left out whole — its rule
 * and every site's override for it — and the cloud keeps it as stored (`mergeAlertSettings`); sending it would be
 * refused, and dropping only the override would erase it.
 */
export function editableAlertSettings(settings: { readonly rules: AlertSettingsWire['rules']; readonly siteOverrides: readonly AlertSettingsWire['siteOverrides'][number][] }): Pick<UpdateAlertSettingsRequestWire, 'rules' | 'siteOverrides'> {
  const types = alertTypeWireSchema.options;
  const unknownSeverity = (severity: string | undefined): boolean => severity === 'unknown';
  const held = new Set(types.filter((type) => unknownSeverity(settings.rules[type]?.severity) || settings.siteOverrides.some((o) => unknownSeverity(o.rules[type]?.severity))));
  const rules: UpdateAlertSettingsRequestWire['rules'] = {};
  for (const type of types) {
    const rule = settings.rules[type];
    if (rule !== undefined && rule.severity !== 'unknown' && !held.has(type)) rules[type] = { enabled: rule.enabled, severity: rule.severity };
  }
  const siteOverrides = settings.siteOverrides.map((o) => {
    const kept: SiteOverrideWire['rules'] = {};
    for (const type of types) {
      const override = o.rules[type];
      if (override === undefined || held.has(type)) continue;
      const { enabled, severity } = override;
      if (severity === 'unknown') continue;
      kept[type] = { ...(enabled === undefined ? {} : { enabled }), ...(severity === undefined ? {} : { severity }) };
    }
    return { siteId: o.siteId, rules: kept };
  });
  return { rules, siteOverrides };
}
